import { cn } from "~/lib/utils";

/**
 * Base CSS classes for source citation badges (NotebookLM-style).
 * Subtle inverted scheme with semi-transparent background.
 * Reused by both the React component and raw HTML generation.
 */
export const SOURCE_BADGE_CLASS =
  "inline-flex items-center justify-center rounded-full px-[0.3em] min-w-[1.4em] h-[1.4em] text-[0.7em] font-normal leading-none no-underline transition-colors align-middle relative top-[-0.05em]";

export const SOURCE_BADGE_ACTIVE_CLASS =
  "bg-muted-foreground/20 text-muted-foreground hover:bg-muted-foreground/30 cursor-pointer";

export const SOURCE_BADGE_DISABLED_CLASS =
  "bg-muted text-muted-foreground/40 cursor-not-allowed";

interface SourceBadgeProps {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  title?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Unified source citation badge used across reports and chat.
 * Renders a compact, rounded badge with a subtle inverted color scheme
 * (semi-transparent muted-foreground background).
 *
 * Follows NotebookLM-style inline citations.
 */
export function SourceBadge({
  children,
  onClick,
  title,
  disabled = false,
  className,
}: SourceBadgeProps) {
  return (
    <span
      role={onClick ? "button" : undefined}
      tabIndex={onClick && !disabled ? 0 : undefined}
      onClick={
        onClick && !disabled
          ? (e) => {
              e.preventDefault();
              onClick(e);
            }
          : undefined
      }
      onKeyDown={
        onClick && !disabled
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e as unknown as React.MouseEvent);
              }
            }
          : undefined
      }
      title={title || undefined}
      className={cn(
        SOURCE_BADGE_CLASS,
        disabled ? SOURCE_BADGE_DISABLED_CLASS : SOURCE_BADGE_ACTIVE_CLASS,
        className
      )}
    >
      {children}
    </span>
  );
}
